<template>
  <v-flex>
      <v-card>
          <v-card-text class="caption grey--text">
              <v-layout>
                  <loading-progress-bar :loading="loading"></loading-progress-bar>
                  <v-spacer></v-spacer>
                  <span>{{facilityName}} {{equipment_name}}</span>
              </v-layout>
          </v-card-text>
          <v-card-text v-if="type==='dygraph'">
              <dygraphs-component :df="df"></dygraphs-component>
          </v-card-text>
          <v-card-text v-else>
              <vue-plotly v-if="!loading" :data="data" :layout="layout" :options="options" ref="plotly"/>
          </v-card-text>
      </v-card>
  </v-flex>
</template>

<script>
import VuePlotly from './VuePlotly'
import {getTimeSeries} from '@/helper'
import DygraphsComponent from './DygraphsComponent'
import LoadingProgressBar from './LoadingProgressBar'
import _ from 'lodash'
import smoothLine from "./mixins/smoothLine";

export default {
  props: {
    title: {type: String, default: ''},
    facility_name: {type: String, default: null},
    equipment_name: {type: String, default: 'Chiller 1'},
    variableX: {type: String, default: 'CHWR_Temp'},
    variableXLabel: {type: String, default: null},
    variableY: {type: String, default: 'CHWS_Temp'},
    dateRange: {type: Object, default: null},
    type: {type: String, default: 'scattergl'},
    onOnly: {type:Boolean, default: false},
    facetGroups: {type: Number, default: 1},
    variableFacet: {type: String, default: 'SWS_Temp'},
    flattenFacet: {type: Boolean, default: false},
    smoothedLine: {type: Boolean, default: false},
    smoothedLineCOPclean: {type: Boolean, default: false},
    opacity: {type: Number, default : 1.0},
    cleanData: {type: Boolean, default: false}
  },
  components: {
    LoadingProgressBar,
    DygraphsComponent,
    VuePlotly
  },
    mixins:[smoothLine],
  computed: {
    layout(){
      let a = {
        title: this.title,
        hovermode: 'closest',
        height: (!this.flattenFacet? 100+this.facetGroups*200: 400),
        xaxis: {
          title: this.variableXLabel ? this.variableXLabel : this.variableX
        },
        yaxis: {
          title: this.type==='histogram'? 'Count' : this.variableY
        },
        grid: {rows: (!this.flattenFacet? this.facetGroups: 1), columns: 1, pattern: 'independent'},
      }

      // fix x, y axis across plots
      a['xaxis'] = {
        range: [this.plotRange.minX, this.plotRange.maxX],
          title: this.variableXLabel ? this.variableXLabel : this.variableX

      }
      a['yaxis'] = {
        range: [this.plotRange.minY, this.plotRange.maxY],
        title: this.type==='histogram'? 'Count' : this.variableY // add yaxis for first row
      }
      for (let i=1; i<=this.facetGroups; i++){
        a['xaxis'+i] = {
          range: [this.plotRange.minX, this.plotRange.maxX],
          title: i==this.facetGroups?  this.variableX : '' // add xaxis for last row
        }
        a['yaxis'+i] = {
          range: [this.plotRange.minY, this.plotRange.maxY]
        }
      }

      return a
    }
  },
  name: 'FacetPlot',
  watch: {
    smoothedLine () {
      this.refresh()
    },
    flattenFacet () {
      this.refresh()
    },
    facility_name () {
      this.refresh()
    },
    variableX () {
      this.refresh()
    },
    variableY () {
      this.refresh()
    },
    equipment_name() {
      this.refresh()
    },
    dateRange () {
      this.refresh()
    },
    onOnly () {
      this.refresh()
    },
    facetGroups () {
      this.refresh()
    },
    variableFacet () {
      this.refresh()
    }
  },
  data: function () {
    return {
      data:[],
      facilityName:this.facility_name,
      options: {},
      timeout:null,
      df: null,
      loading: true,
      plotRange : {
          maxX: 0,
          minX: 100,
          maxY: 0,
          minY: 100
      }

    }
    },
  methods: {
      debounce(callback, wait) {
          clearTimeout(this.timeout);
          this.timeout= setTimeout(() => callback(), wait);
      },
      refresh(){
          this.debounce(this.doRefresh,200);
      },
    async doRefresh() {
        if(this.facility_name){
            this.facilityName=this.facility_name;
        }
      this.loading=true
      if (!!this.variableX & !!this.variableY & !!this.equipment_name & !!this.facilityName){
        let df = await getTimeSeries(
          [this.variableX, this.variableY],
          this.equipment_name,
          this.facilityName,
          this.dateRange,
          null,
          this.onOnly,
          {
            facet_var: this.variableFacet,
            facet_groups: this.facetGroups
          },
          this.cleanData
        )
        this.df = df
        let output = []
        let k=1 // facet index
        let m=0 //color index
        let maxX = []
        let minX = []
        let maxY = []
        let minY = []
        let colorList = ['#1f77b4',  // muted blue
                        '#ff7f0e',  // safety orange
                        '#2ca02c',  // cooked asparagus green
                        '#d62728',  // brick red
                        '#9467bd',  // muted purple
                        '#8c564b',  // chestnut brown
                        '#e377c2',  // raspberry yogurt pink
                        '#7f7f7f',  // middle gray
                        '#bcbd22',  // curry yellow-green
                        '#17becf']

        for (let i in df['data']) {
          maxX.push(_.max(df['data'][i][this.variableX]))
          minX.push(_.min(df['data'][i][this.variableX]))
          maxY.push(_.max(df['data'][i][this.variableY]))
          minY.push(_.min(df['data'][i][this.variableY]))
          output.push(
            {
              marker: {
                opacity: this.opacity,
                color: colorList[m % 10]
              },
              x: df['data'][i][this.variableX],
              y: df['data'][i][this.variableY],
              type: this.type,
              mode: 'markers', name: df['facet_var']+' '+i,
              yaxis: 'y'+k,
              xaxis: 'x'+k
            }
          )

          if (this.smoothedLine) {
              let options = {span: 0.6, band: 0.8, degree: 1}

              let newx = []
              let newy = []
              let isViable = false
              if (this.smoothedLineCOPclean) {
                  for (let j = 0; j < df['data'][i][this.variableX].length; j++) {
                      if (df['data'][i][this.variableX][j]) {
                          if (df['data'][i][this.variableY][j] > 0 & df['data'][i][this.variableY][j] < 20) {
                              newx.push(df['data'][i][this.variableX][j])
                              newy.push(df['data'][i][this.variableY][j])
                              isViable = true
                          }
                      }
                  }
              }
              if (isViable) {
                  let smooth = this.smoothLineLoess(newx, newy, 50, options)
                  output.push({
                      line: {
                          color: colorList[m % 10]
                      },
                      x: smooth.data['x'],
                      y: smooth.fitted['fitted'],
                      type: this.type,
                      mode: 'lines',
                      name: 'Smoothed Line of ' + df['facet_var'] + ' ' + i,
                      yaxis: 'y' + k,
                      xaxis: 'x' + k
                  })
              }

          }

          m++
          if (!this.flattenFacet) {
            k++
          }
        }
        this.data = output
        this.plotRange = {
          maxX: _.max(maxX),
          minX: _.min(minX),
          maxY: _.max(maxY),
          minY: _.min(minY)
      }

      }
      this.loading=false
    }
  },
   mounted () {
      if (!this.facilityName) {
          this.facilityName = this.$store.state.pointsListStore.pointsList.facility_name
      }
     this.refresh()
  }
}

</script>

<style scoped>

</style>
